// Extract plain text from an uploaded resume file.
// Text-based PDFs: parsed client-side via pdfjs-dist.
// Image-based PDFs: pages rendered to images, sent to /api/ocr for AI-powered OCR.
// Also extracts candidate photo if present (cropped to 512x512).

import * as pdfjsLib from "pdfjs-dist";
import { buildAuthHeader } from "@/types";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

export interface ExtractionResult {
  text: string;
  photo: string | null; // base64 data-URL of 512x512 JPEG, or null
  fileBase64: string | null; // base64 (no data-URL prefix) of original file for R2 storage
  fileMime: string | null;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000; // 32 KB
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + chunk) as unknown as number[]
    );
  }
  return btoa(binary);
}

function inferMime(file: File): string {
  if (file.type) return file.type;
  const lower = file.name.toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".md")) return "text/markdown";
  return "application/octet-stream";
}

export async function extractTextFromFile(
  file: File,
  onStatus?: (status: string) => void
): Promise<ExtractionResult> {
  const name = file.name.toLowerCase();

  // Cache the original bytes (≤8 MB cap to avoid blowing the JSON body limit).
  let fileBase64: string | null = null;
  let fileMime: string | null = null;
  if (file.size > 0 && file.size <= 8 * 1024 * 1024) {
    try {
      const buf = await file.arrayBuffer();
      fileBase64 = arrayBufferToBase64(buf);
      fileMime = inferMime(file);
    } catch {
      // ignore; we'll just skip the R2 upload
    }
  }

  if (name.endsWith(".pdf") || file.type === "application/pdf") {
    const r = await extractPdf(file, onStatus);
    return { ...r, fileBase64, fileMime };
  }
  if (
    name.endsWith(".txt") ||
    name.endsWith(".md") ||
    file.type.startsWith("text/")
  ) {
    return { text: await file.text(), photo: null, fileBase64, fileMime };
  }
  throw new Error(
    `Unsupported file type: ${file.type || name}. Upload a PDF or plain-text file.`
  );
}

async function extractPdf(
  file: File,
  onStatus?: (status: string) => void
): Promise<{ text: string; photo: string | null }> {
  const buf = await file.arrayBuffer();
  if (buf.byteLength === 0) {
    throw new Error("PDF file is empty (0 bytes).");
  }

  onStatus?.("Parsing PDF…");
  const data = new Uint8Array(buf);

  let doc: pdfjsLib.PDFDocumentProxy;
  try {
    doc = await pdfjsLib.getDocument({
      data: data.slice(0),
      useWorkerFetch: false,
      isEvalSupported: false,
      useSystemFonts: true,
    }).promise;
  } catch (e) {
    throw new Error(
      `Failed to parse PDF: ${e instanceof Error ? e.message : String(e)}`
    );
  }

  if (doc.numPages === 0) {
    throw new Error("PDF has no pages.");
  }

  // Render first page for photo extraction (and possibly OCR)
  onStatus?.("Rendering first page…");
  const firstPage = await renderPageWithDimensions(doc, 1);

  // Start photo extraction in parallel with text extraction
  const photoPromise = firstPage
    ? extractPhoto(firstPage.dataUrl, firstPage.width, firstPage.height, onStatus)
    : Promise.resolve(null);

  // Step 1: Try text extraction
  onStatus?.("Extracting text…");
  let textResult = await extractTextFromPages(doc);

  if (!textResult || textResult.length < 50) {
    // Step 2: Fall back to OCR
    onStatus?.("Image-based PDF detected. Running OCR…");
    const images = await renderPagesToImages(doc);
    if (images.length === 0) {
      throw new Error("Could not render PDF pages to images.");
    }
    onStatus?.(`Sending ${images.length} page(s) to AI for text recognition…`);
    const ocrText = await callOcrApi(images);
    if (!ocrText || ocrText.length < 20) {
      throw new Error(
        "OCR could not extract enough text from this PDF. Please try a clearer document."
      );
    }
    textResult = ocrText;
  }

  const photo = await photoPromise;

  return { text: textResult, photo };
}

async function extractPhoto(
  pageImage: string,
  pageWidth: number,
  pageHeight: number,
  onStatus?: (status: string) => void
): Promise<string | null> {
  try {
    onStatus?.("Detecting candidate photo…");
    const res = await fetch("/api/extract-photo", {
      method: "POST",
      headers: { "content-type": "application/json", ...buildAuthHeader() },
      body: JSON.stringify({
        image: pageImage,
        page_width: pageWidth,
        page_height: pageHeight,
      }),
    });

    if (!res.ok) return null;
    const box = (await res.json()) as {
      found: boolean;
      x: number;
      y: number;
      w: number;
      h: number;
    };
    if (!box.found) return null;

    onStatus?.("Cropping candidate photo…");
    return await cropPhoto(pageImage, box);
  } catch {
    return null;
  }
}

async function cropPhoto(
  pageImage: string,
  box: { x: number; y: number; w: number; h: number }
): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Pixel coordinates from Claude — clamp to image bounds
      let sx = Math.max(0, box.x);
      let sy = Math.max(0, box.y);
      let sw = Math.min(box.w, img.width - sx);
      let sh = Math.min(box.h, img.height - sy);

      // Sanity check
      if (sw < 10 || sh < 10) {
        resolve(null);
        return;
      }

      // Make it square — center-crop the longer side
      const side = Math.min(sw, sh);
      const cx = sx + (sw - side) / 2;
      const cy = sy + (sh - side) / 2;

      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(null);
        return;
      }

      ctx.drawImage(img, cx, cy, side, side, 0, 0, 512, 512);

      // Verify the crop isn't blank (all white/transparent)
      const sample = ctx.getImageData(128, 128, 256, 256).data;
      let nonWhitePixels = 0;
      for (let i = 0; i < sample.length; i += 4) {
        if (sample[i] < 240 || sample[i + 1] < 240 || sample[i + 2] < 240) {
          nonWhitePixels++;
        }
      }
      // If less than 5% of sampled pixels are non-white, it's blank
      if (nonWhitePixels < (256 * 256) * 0.05) {
        resolve(null);
        return;
      }

      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => resolve(null);
    img.src = pageImage;
  });
}

interface PageRender {
  dataUrl: string;
  width: number;
  height: number;
}

async function renderPageWithDimensions(
  doc: pdfjsLib.PDFDocumentProxy,
  pageNum: number
): Promise<PageRender | null> {
  try {
    const page = await doc.getPage(pageNum);
    const scale = 1.5;
    const MAX_DIM = 1600;
    let viewport = page.getViewport({ scale });

    if (viewport.width > MAX_DIM || viewport.height > MAX_DIM) {
      const ratio = Math.min(MAX_DIM / viewport.width, MAX_DIM / viewport.height);
      viewport = page.getViewport({ scale: scale * ratio });
    }

    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    await page.render({ canvasContext: ctx, viewport }).promise;
    return {
      dataUrl: canvas.toDataURL("image/jpeg", 0.7),
      width: canvas.width,
      height: canvas.height,
    };
  } catch {
    return null;
  }
}

async function renderPageToImage(
  doc: pdfjsLib.PDFDocumentProxy,
  pageNum: number
): Promise<string | null> {
  const result = await renderPageWithDimensions(doc, pageNum);
  return result?.dataUrl ?? null;
}

interface TextItem { str: string; x: number; y: number; width: number; }

// Group items into visual lines by Y proximity, then sort each line left-to-right.
function groupIntoLines(items: TextItem[], yTolerance = 4): TextItem[][] {
  if (items.length === 0) return [];
  const sorted = [...items].sort((a, b) => b.y - a.y);
  const groups: TextItem[][] = [[sorted[0]]];
  for (let i = 1; i < sorted.length; i++) {
    const last = groups[groups.length - 1];
    if (Math.abs(sorted[i].y - last[0].y) <= yTolerance) {
      last.push(sorted[i]);
    } else {
      groups.push([sorted[i]]);
    }
  }
  for (const g of groups) g.sort((a, b) => a.x - b.x);
  return groups;
}

// Join text items with gap-aware concatenation.
// PDFs sometimes store a single word as multiple adjacent text items (e.g. "Man"+"ila").
// When the gap between item[i].end and item[i+1].start is < 1.5 pt they are
// visually touching → concatenate directly; otherwise insert a space.
function joinItems(items: TextItem[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0].str;
  const sorted = [...items].sort((a, b) => a.x - b.x);
  let out = sorted[0].str;
  for (let i = 1; i < sorted.length; i++) {
    const gap = sorted[i].x - (sorted[i - 1].x + sorted[i - 1].width);
    out += gap < 1.5 ? sorted[i].str : " " + sorted[i].str;
  }
  return out;
}

// When minColGapPts is provided, splits a visual line into multiple text lines
// wherever the physical gap between consecutive items exceeds the threshold.
// This handles mixed-layout pages where items from two columns land on the same
// Y-band but have a large horizontal gap between them.
// Adjacent segments that look like a word split across the column boundary
// (prev ends with a letter, next starts with lowercase) are joined directly.
function linesToText(lineGroups: TextItem[][], minColGapPts?: number): string {
  return lineGroups
    .map(g => {
      if (!minColGapPts || g.length <= 1) {
        return joinItems(g).trim();
      }
      const sorted = [...g].sort((a, b) => a.x - b.x);
      const segs: TextItem[][] = [[sorted[0]]];
      let prevEnd = sorted[0].x + sorted[0].width;
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].x - prevEnd > minColGapPts) segs.push([]);
        segs[segs.length - 1].push(sorted[i]);
        prevEnd = sorted[i].x + sorted[i].width;
      }
      const segTexts = segs.map(s => joinItems(s).trim()).filter(Boolean);
      // Detect word fragments split across the column gap and join them:
      // if prev segment ends with a letter and cur starts with lowercase → concatenate.
      const out: string[] = [];
      for (const seg of segTexts) {
        if (out.length > 0 && /[a-zA-Z]$/.test(out[out.length - 1]) && /^[a-z]/.test(seg)) {
          out[out.length - 1] += seg;
        } else {
          out.push(seg);
        }
      }
      return out.join("\n");
    })
    .filter(Boolean)
    .join("\n");
}

// Returns the X boundary separating two columns, or null for single-column pages.
// Strict threshold (avg*0.3) prevents false positives on pages with a photo or
// uneven X distribution that aren't truly two-column.
function _detectBoundaryCore(items: TextItem[], pageWidth: number): number | null {
  if (items.length < 10) return null;
  const BINS = 40;
  const bw = pageWidth / BINS;
  const counts = new Array(BINS).fill(0);
  for (const it of items) counts[Math.min(Math.floor(it.x / bw), BINS - 1)]++;
  const lo = Math.floor(BINS * 0.25);
  const hi = Math.ceil(BINS * 0.75);
  let gapBin = lo;
  for (let b = lo + 1; b <= hi; b++) {
    if (counts[b] < counts[gapBin]) gapBin = b;
  }
  const avg = items.length / BINS;
  if (counts[gapBin] > avg * 0.3) return null;
  const boundary = (gapBin + 0.5) * bw;
  const leftItems = items.filter(it => it.x < boundary);
  const rightItems = items.filter(it => it.x >= boundary);
  if (leftItems.length < items.length * 0.10 || rightItems.length < items.length * 0.10) return null;

  // Verify the boundary is a genuine whitespace gap, not a statistical artifact
  // on a single-column page where long lines happen to produce a sparse histogram bin.
  // For each Y band that has items on both sides, measure the gap between the right
  // edge of the leftmost group and the left edge of the rightmost group.
  // True column gutters are ≥ 15 pt wide; word-space-sized gaps (~3–8 pt) indicate
  // continuous text spanning the detected boundary (false positive).
  const Y_BUCKET = 4; // pt — same tolerance as groupIntoLines
  const MIN_COL_GAP = 15; // pt — real column gutter threshold
  const leftByBucket = new Map<number, TextItem[]>();
  for (const it of leftItems) {
    const b = Math.round(it.y / Y_BUCKET);
    if (!leftByBucket.has(b)) leftByBucket.set(b, []);
    leftByBucket.get(b)!.push(it);
  }
  let sharedBands = 0;
  let narrowGapBands = 0;
  for (const [bucket, lits] of leftByBucket) {
    const rits = rightItems.filter(it => Math.abs(Math.round(it.y / Y_BUCKET) - bucket) <= 1);
    if (rits.length === 0) continue;
    sharedBands++;
    const leftEnd = Math.max(...lits.map(it => it.x + (it.width || 0)));
    const rightStart = Math.min(...rits.map(it => it.x));
    if (rightStart - leftEnd < MIN_COL_GAP) narrowGapBands++;
  }
  // If most shared-Y bands have tightly packed items (no real gutter), it's not
  // a genuine two-column boundary — single-column text flowing across the page.
  if (sharedBands >= 3 && narrowGapBands > sharedBands * 0.5) return null;

  return boundary;
}

// First tries the whole page; if that fails, retries on the middle 50% of items
// by Y to handle pages where a full-width header masks the two-column body gap.
function findColumnBoundary(items: TextItem[], pageWidth: number): number | null {
  if (items.length < 15) return null;
  const b = _detectBoundaryCore(items, pageWidth);
  if (b !== null) return b;
  const sorted = [...items].sort((a, b) => a.y - b.y);
  const lo = Math.floor(sorted.length * 0.25);
  const hi = Math.ceil(sorted.length * 0.75);
  return _detectBoundaryCore(sorted.slice(lo, hi), pageWidth);
}

async function extractTextFromPages(
  doc: pdfjsLib.PDFDocumentProxy
): Promise<string> {
  const parts: string[] = [];

  for (let i = 1; i <= doc.numPages; i++) {
    try {
      const page = await doc.getPage(i);
      const [content, viewport] = await Promise.all([
        page.getTextContent(),
        Promise.resolve(page.getViewport({ scale: 1 })),
      ]);
      const pageWidth = viewport.width;

      const items: TextItem[] = [];
      for (const item of content.items) {
        if (!("str" in item) || typeof item.str !== "string" || !item.str.trim()) continue;
        const t = (item as any).transform;
        if (!Array.isArray(t) || t.length < 6) continue;
        items.push({ str: item.str, x: t[4], y: t[5], width: (item as any).width ?? 0 });
      }
      if (items.length === 0) continue;

      const boundary = findColumnBoundary(items, pageWidth);
      let pageText: string;

      if (boundary !== null) {
        // Use item midpoint so wide items whose start-x crosses the boundary
        // (e.g. the last word of a full-width line) land in the correct column.
        const leftItems = items.filter(it => it.x + it.width / 2 < boundary);
        const rightItems = items.filter(it => it.x + it.width / 2 >= boundary);

        // Robust "near-top Y": skip the top ~10 % of each column's items so that
        // a handful of rogue items (wide section headers whose midpoint lands in the
        // wrong column, or PDF-artifact items stored at incorrect coordinates) cannot
        // inflate maxY and falsely trigger the paired-layout path.
        const nearTopY = (arr: TextItem[]) => {
          if (!arr.length) return 0;
          const ys = arr.map(it => it.y).sort((a, b) => b - a);
          return ys[Math.min(Math.max(1, Math.floor(arr.length * 0.10)), ys.length - 1)];
        };
        const maxYLeft = nearTopY(leftItems);
        const maxYRight = nearTopY(rightItems);

        // PAIRED layout: both columns start at roughly the same Y AND a significant
        // fraction of Y-bands have items in both columns (labels/dates on left align
        // row-by-row with descriptions on right).
        // Merge all items and let linesToText split them at the column gap (20 pt).
        //
        // INDEPENDENT/SIDEBAR layout: columns start at different Y positions OR
        // most Y-bands have items in only one column (sidebar with contact/skills).
        // Output the primary (higher-start) column first, then the secondary.
        const PAIRED_TOP_THRESHOLD = 40; // pt — tuned on Ellaine (paired) vs Luz (sidebar)
        const PAIRED_RATIO_THRESHOLD = 0.30; // min 30% of Y-bands shared by both columns
        const Y_BKT = 4;
        const leftBkts = new Set(leftItems.map(it => Math.round(it.y / Y_BKT)));
        const rightBkts = new Set(rightItems.map(it => Math.round(it.y / Y_BKT)));
        let sharedBands = 0;
        for (const b of leftBkts) {
          if (rightBkts.has(b) || rightBkts.has(b + 1) || rightBkts.has(b - 1)) sharedBands++;
        }
        const totalBands = new Set([...leftBkts, ...rightBkts]).size;
        const pairedRatio = totalBands > 0 ? sharedBands / totalBands : 0;
        if (Math.abs(maxYLeft - maxYRight) < PAIRED_TOP_THRESHOLD && pairedRatio >= PAIRED_RATIO_THRESHOLD) {
          pageText = linesToText(groupIntoLines([...leftItems, ...rightItems]), 20);
        } else {
          const [primaryLines, secondaryLines] =
            maxYLeft >= maxYRight
              ? [groupIntoLines(leftItems), groupIntoLines(rightItems)]
              : [groupIntoLines(rightItems), groupIntoLines(leftItems)];
          pageText = `${linesToText(primaryLines)}\n\n${linesToText(secondaryLines)}`;
        }
      } else {
        // Single-column or mixed-layout page — use inline gap detection (20 pt)
        // to separate items from distinct columns that share the same visual line.
        pageText = linesToText(groupIntoLines(items), 20);
      }

      if (pageText) parts.push(pageText);
    } catch {
      // Skip pages that fail
    }
  }

  return parts
    .join("\n\n")
    .replace(/[ \t]+/g, " ")
    .replace(/ \n/g, "\n")
    // Remove standalone page numbers
    .replace(/^[ \t]*(?:page\s+)?\d{1,3}[ \t]*$/gim, "")
    // Ensure bullet/list marker characters start their own line when they
    // appear inline (e.g. "Some text• Next item" → "Some text\n• Next item").
    // Handles •, ●, ◦, ✓, ✔, ►, ➢, ➤, →, ✗, ✖ but not plain "-" to avoid
    // splitting date ranges and hyphenated words.
    .replace(/([^\n])(\s*[•●◦✓✔►➢➤→✗✖](?:\s|$))/g, "$1\n$2")
    // Numbered list items not at line start: "some text 1. Next item" → "…\n1. …"
    // Only when followed by a capital letter to avoid splitting version numbers.
    .replace(/([^\n])(\s*\d+[.)]\s+)(?=[A-Z])/g, "$1\n$2")
    // Add a blank line before likely section headers: lines that are entirely
    // uppercase (3–60 chars, may contain spaces, &, /, -) and don't already
    // have a blank line before them.
    .replace(/(?<!\n)\n([ \t]*[A-Z][A-Z\s&/\\-]{2,58}[A-Z][ \t]*)\n/g, "\n\n$1\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

async function renderPagesToImages(
  doc: pdfjsLib.PDFDocumentProxy
): Promise<string[]> {
  const images: string[] = [];
  const maxPages = Math.min(doc.numPages, 10);

  for (let i = 1; i <= maxPages; i++) {
    const img = await renderPageToImage(doc, i);
    if (img) images.push(img);
  }

  return images;
}

async function callOcrApi(images: string[]): Promise<string> {
  const res = await fetch("/api/ocr", {
    method: "POST",
    headers: { "content-type": "application/json", ...buildAuthHeader() },
    body: JSON.stringify({ images }),
  });

  if (!res.ok) {
    let detail = "";
    try {
      const err = await res.json();
      detail = (err as any).error || JSON.stringify(err);
    } catch {
      detail = await res.text();
    }
    throw new Error(`OCR failed: ${detail}`);
  }

  const data = (await res.json()) as { text: string };
  return data.text;
}
